import * as React from 'react'
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { BillingCardSetupDto, BillingHistoryDto, BillingPurchaseDto, BillingRefundDto, BillingSummaryDto } from '@lib/dto/billing.dto'
import type {
  CreateCardSetupInput,
  CreatePurchaseInput,
  CreateRefundInput,
  SetAutoReloadInput,
  SetLowBalanceThresholdInput,
} from '@lib/input/billing.input'
import type { Uuid } from '@lib/validators/params'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { billingKeys, teamKeys } from '@/hooks/queryKeys'
import { keepPreviousWithin } from '@/hooks/keepPreviousWithin'
import { BillingService } from '@/services/BillingService'

/** How often a purchase back from Checkout is re-read while Stripe hasn't settled it. */
const PURCHASE_POLL_MS = 1_000

function useScopedService(): BillingService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new BillingService(client), [client])
}

export function useBillingSummary(): UseQueryResult<BillingSummaryDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({ queryKey: billingKeys.summary(tenantId, cacheKey), queryFn: () => service.summary(tenantId) })
}

/** One page of the billing history, newest first; `before` is a previous page's `nextBefore`. */
export function useBillingHistory(before: string | null): UseQueryResult<BillingHistoryDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: billingKeys.history(tenantId, cacheKey, before),
    queryFn: () => service.history(tenantId, before),
    placeholderData: keepPreviousWithin(billingKeys.historyScope(tenantId, cacheKey)),
  })
}

/**
 * A purchase back from Stripe Checkout (none while `purchaseId` is null). Each
 * read settles it from Stripe, so it's re-read every second while still pending
 * and nothing has said why credit was not added.
 */
export function useBillingPurchase(purchaseId: Uuid | null): UseQueryResult<BillingPurchaseDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: billingKeys.purchase(tenantId, cacheKey, purchaseId),
    queryFn: purchaseId === null ? skipToken : () => service.purchase(tenantId, purchaseId),
    refetchInterval: (query) => {
      const purchase = query.state.data
      if (purchase?.status !== 'pending' || purchase.failureMessage !== null) return false
      return PURCHASE_POLL_MS
    },
    retry: false,
  })
}

/** A card setup back from Stripe's page (none while `setupId` is null), re-read every second while still pending. */
export function useCardSetup(setupId: Uuid | null): UseQueryResult<BillingCardSetupDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: billingKeys.cardSetup(tenantId, cacheKey, setupId),
    queryFn: setupId === null ? skipToken : () => service.cardSetup(tenantId, setupId),
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? PURCHASE_POLL_MS : false),
    retry: false,
  })
}

/** An Owner buys and refunds credit and sets the low-balance alert. Each settled change refreshes the balance, history, and activity log. */
export function useBillingActions(): {
  createPurchase: UseMutationResult<BillingPurchaseDto, Error, CreatePurchaseInput>
  cancelPurchase: UseMutationResult<BillingPurchaseDto, Error, Uuid>
  createRefund: UseMutationResult<BillingRefundDto, Error, CreateRefundInput>
  setLowBalanceThreshold: UseMutationResult<BillingSummaryDto, Error, SetLowBalanceThresholdInput>
  setAutoReload: UseMutationResult<BillingSummaryDto, Error, SetAutoReloadInput>
  createCardSetup: UseMutationResult<BillingCardSetupDto, Error, CreateCardSetupInput>
  cancelCardSetup: UseMutationResult<BillingCardSetupDto, Error, Uuid>
  /** Re-reads the balance, history, and activity log, after credit lands. */
  refresh: () => Promise<void>
} {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedService()
  const queryClient = useQueryClient()
  const refresh = React.useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: billingKeys.summary(tenantId, cacheKey) }),
      queryClient.invalidateQueries({ queryKey: billingKeys.historyScope(tenantId, cacheKey) }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activity(tenantId, cacheKey) }),
    ])
  }, [queryClient, tenantId, cacheKey])
  return {
    // Nothing to refresh: the Owner leaves for Stripe's page next.
    createPurchase: useMutation({ mutationFn: (input) => service.createPurchase(tenantId, input) }),
    cancelPurchase: useMutation({ mutationFn: (purchaseId) => service.cancelPurchase(tenantId, purchaseId), onSuccess: refresh }),
    createRefund: useMutation({ mutationFn: (input) => service.createRefund(tenantId, input), onSuccess: refresh }),
    setLowBalanceThreshold: useMutation({ mutationFn: (input) => service.setLowBalanceThreshold(tenantId, input), onSuccess: refresh }),
    setAutoReload: useMutation({ mutationFn: (input) => service.setAutoReload(tenantId, input), onSuccess: refresh }),
    // Nothing to refresh: the Owner leaves for Stripe's page next.
    createCardSetup: useMutation({ mutationFn: (input) => service.createCardSetup(tenantId, input) }),
    cancelCardSetup: useMutation({ mutationFn: (setupId) => service.cancelCardSetup(tenantId, setupId), onSuccess: refresh }),
    refresh,
  }
}
